/**
 * BatchesTab - Lists all batch uploads for a dataset (newest first).
 * Each batch corresponds to a FilesTable row with SK `BATCH#<batchId>`.
 */

import React, { useEffect, useState, useCallback } from 'react';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Header from '@amzn/awsui-components-console/header';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import Alert from '@amzn/awsui-components-console/alert';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../../utils/apiClient';

interface Batch {
  batchId: string;
  snapshotDate: string;
  fileId: string;
  mode: 'create' | 'append';
  status: 'pending' | 'transforming' | 'ready' | 'failed';
  glueJobRunId?: string;
  rowCount?: number;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

interface BatchesTabProps {
  datasetId: string;
  refreshKey?: number;
  onAppendClick?: () => void;
}

const getStatusType = (status: string): 'success' | 'warning' | 'error' | 'info' | 'loading' | 'pending' => {
  switch (status) {
    case 'ready': return 'success';
    case 'transforming': return 'loading';
    case 'pending': return 'pending';
    case 'failed': return 'error';
    default: return 'info';
  }
};

export const BatchesTab: React.FC<BatchesTabProps> = ({ datasetId, refreshKey = 0, onAppendClick }) => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(datasetId)}/batches`), { ...apiFetchDefaults });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `HTTP ${r.status}`); }
      const data = await r.json();
      setBatches(Array.isArray(data.batches) ? data.batches : []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Table
      header={
        <Header
          variant="h2"
          counter={batches.length > 0 ? `(${batches.length})` : undefined}
          description="Each batch is one file upload. First batch creates the Glue+Andes tables; subsequent batches append a new snapshot_date partition."
          actions={<SpaceBetween direction="horizontal" size="xs"><Button iconName="refresh" onClick={load} loading={loading}>Refresh</Button>{onAppendClick && <Button variant="primary" iconName="upload" onClick={onAppendClick}>Upload new snapshot</Button>}</SpaceBetween>}
        >
          Batches
        </Header>
      }
      items={batches}
      loading={loading}
      loadingText="Loading batches..."
      columnDefinitions={[
        {
          id: 'batchId',
          header: 'Batch ID',
          cell: (b: Batch) => <Box fontWeight="bold">{b.batchId}</Box>,
          minWidth: 160,
        },
        {
          id: 'snapshotDate',
          header: 'Snapshot date',
          cell: (b: Batch) => b.snapshotDate,
          minWidth: 110,
        },
        {
          id: 'mode',
          header: 'Mode',
          cell: (b: Batch) => b.mode === 'create'
            ? <StatusIndicator type="info">create</StatusIndicator>
            : <StatusIndicator type="success">append</StatusIndicator>,
          minWidth: 80,
        },
        {
          id: 'status',
          header: 'Status',
          cell: (b: Batch) => <StatusIndicator type={getStatusType(b.status)}>{b.status}</StatusIndicator>,
          minWidth: 100,
        },
        {
          id: 'rowCount',
          header: 'Rows',
          cell: (b: Batch) => b.rowCount?.toLocaleString() ?? '-',
          minWidth: 80,
        },
        {
          id: 'glueJobRunId',
          header: 'Glue job run',
          cell: (b: Batch) => b.glueJobRunId ? <code style={{ fontSize: '11px' }}>{b.glueJobRunId.slice(0, 16)}...</code> : '-',
          minWidth: 180,
        },
        {
          id: 'createdAt',
          header: 'Created',
          cell: (b: Batch) => new Date(b.createdAt).toLocaleString(),
          minWidth: 160,
        },
      ]}
      empty={
        error
          ? <Alert type="error">{error}</Alert>
          : (
            <Box textAlign="center" color="inherit" padding="m">
              <Box variant="strong">No batches yet</Box>
              <Box variant="p" color="inherit">
                Upload a file from the "Upload new snapshot" button above to start.
              </Box>
            </Box>
          )
      }
      variant="embedded"
      stickyHeader
    />
  );
};

export default BatchesTab;
